import React, { useState, Fragment, useEffect, useMemo } from 'react'
import injectStyles from 'react-jss'

import {
  Box,
  Button,
  Fab,
  Grid,
  IconButton,
  Input,
  Menu,
  MenuItem,
} from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import MoreVertIcon from '@material-ui/icons/MoreVert'
import RichTextEditor from '../../../../../../../../../../../Components/RichTextEditor'
import { styles } from './styles'
import { useEditSequenceContext } from '../../../context/EditSequenceContext'
import { TimingSelect } from '../TimingSelect'
import {
  calculateTimingInSeconds,
  calculateTimingType,
  calculateTimingValue,
} from '../../../../../../utils'
import { EmailDialog } from '../../../../../../../../../../../Components/EmailDialog/EmailDialog'
import { validPromofyEmail } from '../../../../../../../../../../../shared/validation'
import { sendActionSimulation } from '../../../../../../gql'
import { ACTION } from '../../../../../../../../../../../shared/constants'

const EmailTemplate = ({
  emailId,
  actionId,
  email,
  action,
  classes,
  onDelete,
  controlModal,
  handleDrawerToggle,
  totalActions,
  index,
  seqStatus,
}) => {
  const isDraft = isNaN(actionId)
  const isSendTestMessageDisabled = isDraft || seqStatus === 'ARCHIVED'
  const {
    saveOrUpdateAction,
    editSequenceContextState,
  } = useEditSequenceContext()
  const {
    sequence: { DateSequenceTemplate, hasScheduledTrigger, Trigger },
  } = editSequenceContextState
  let timingTypeLabelText =
    DateSequenceTemplate.length > 0 ? 'after scheduled start time' : ''
  if (!timingTypeLabelText && Trigger.length > 0) {
    timingTypeLabelText = hasScheduledTrigger
      ? 'before trigger'
      : 'after trigger'
  }

  const timeDelay = useMemo(
    () => (hasScheduledTrigger ? action.time_before : action.time_delay),
    [hasScheduledTrigger, action.time_before, action.time_delay],
  )
  const [openSendEmailDialog, setOpenSendEmailDialog] = useState(false)
  const [emailInputError, setEmailInputError] = useState('')
  const [emailInputSuccess, setEmailInputSuccess] = useState('')
  const [testEmail, setTestEmail] = useState('')
  const [isLoadingTestEmail, setIsLoadingTestEmail] = useState(false)

  const [timingType, setTimingType] = useState(calculateTimingType(timeDelay))
  const [timingValue, setTimingValue] = useState(
    calculateTimingValue(timeDelay),
  )

  const timing = useMemo(() => {
    return calculateTimingInSeconds({ timingType, timingValue })
  }, [timingType, timingValue])

  const [textAreaSettings, setTextAreaSettings] = useState({
    title: 'See more',
    rows: 4,
  })
  const [anchorEl, setAnchorEl] = React.useState(null)

  const { name, subject, body } = email || {
    'name': '',
    'subject': '',
    'body': '',
  }

  const open = Boolean(anchorEl)
  const ITEM_HEIGHT = 48

  const handleClick = event => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const onSeeMoreClick = () => {
    if (textAreaSettings.title === 'See more') {
      return setTextAreaSettings({ title: 'Close', rows: 'auto' })
    }
    setTextAreaSettings({
      title: 'See more',
      rows: 4,
    })
  }

  const onChangeEmailInput = (value, error) => {
    if (error) {
      setEmailInputError(error)
    } else {
      const isNotPromofyEmail = validPromofyEmail(
        value,
        'Please provide @oppizi.com email',
      )
      setEmailInputError(isNotPromofyEmail)
    }
    setTestEmail(value)
  }

  const closeEmailDialog = () => {
    setOpenSendEmailDialog(false)
    setTimeout(() => {
      setEmailInputSuccess('')
      setEmailInputError('')
      setTestEmail('')
    }, 1000)
  }

  const sendTestEmail = async () => {
    try {
      setIsLoadingTestEmail(true)
      await sendActionSimulation({
        action: ACTION.EMAIL,
        actionId,
        receiver: testEmail,
      })
      setIsLoadingTestEmail(false)
      setEmailInputError('')
      setEmailInputSuccess('Message sent')
    } catch (error) {
      console.error(error)
      setIsLoadingTestEmail(false)
      setEmailInputError(error.message)
      setEmailInputSuccess('')
    }
  }

  useEffect(() => {
    saveOrUpdateAction({ actionId, timeDelay: String(timing) })
  }, [timing])

  useEffect(() => {
    setTimingType(calculateTimingType(timeDelay))
    setTimingValue(calculateTimingValue(timeDelay))
  }, [timeDelay])

  const popupOptions = [
    {
      disabled: false,
      id: 1,
      label: 'Edit',
      func: () => {
        handleClose()
        controlModal({
          eId: emailId,
          aId: actionId,
          open: true,
          cType: 'email',
        })
      },
    },
    {
      disabled: false,
      id: 2,
      label: 'Delete',
      func: () => {
        handleClose()
        onDelete(actionId, 'email')
      },
    },
    {
      disabled: isSendTestMessageDisabled,
      id: 3,
      label: 'Send test message',
      func: () => {
        handleClose()
        setOpenSendEmailDialog(true)
      },
    },
  ]

  return (
    <Fragment>
      <EmailDialog
        isOpen={openSendEmailDialog}
        isLoading={isLoadingTestEmail}
        error={Boolean(emailInputError)}
        helperText={emailInputError || emailInputSuccess}
        onClose={closeEmailDialog}
        title="Send Test Email"
        contentText="Please provide test user's email"
        onSend={sendTestEmail}
        onCancel={closeEmailDialog}
        onChange={onChangeEmailInput}
        leftButtonLabel="Cancel"
        rightButtonLabel="Send"
        cypress_id="EP-send-test-email-dialog"
        succeed={Boolean(emailInputSuccess)}
        value={testEmail}
      />
      <Grid container direction="column" alignItems="center">
        <div>
          <Grid item xs>
            <div className={classes.line} />
          </Grid>
        </div>
        <Grid item>
          <div className={classes.header}>
            {`Automated email ${isDraft ? '[DRAFT]' : ''}`}
            <div>
              <IconButton onClick={handleClick}>
                <MoreVertIcon style={{ fill: 'white' }} />
              </IconButton>
              <Menu
                id="long-menu"
                anchorEl={anchorEl}
                keepMounted
                open={open}
                onClose={handleClose}
                PaperProps={{
                  style: {
                    maxHeight: ITEM_HEIGHT * 4.5,
                    width: '9em',
                  },
                }}
              >
                {popupOptions.map(item => (
                  <MenuItem
                    disabled={item.disabled}
                    key={item.label}
                    onClick={() => item.func()}
                  >
                    <div className={classes.popupItem}>{item.label}</div>
                  </MenuItem>
                ))}
              </Menu>
            </div>
          </div>
        </Grid>
        <Grid item>
          <div className={classes.body}>
            <Grid container direction="column">
              <div className={classes.wrapperInput}>
                <Box>Name:</Box>
                <Input
                  value={name}
                  size="small"
                  className={classes.input}
                  variant="filled"
                  fullWidth
                  disableUnderline={true}
                  disabled={true}
                />
              </div>
              <div className={classes.wrapperInput}>
                <Box>Subject:</Box>
                <Input
                  size="small"
                  className={classes.input}
                  variant="filled"
                  fullWidth
                  disableUnderline={true}
                  disabled={true}
                  value={subject}
                />
              </div>
              <TimingSelect
                timingTypeLabelText={timingTypeLabelText}
                timingType={timingType}
                setTimingType={setTimingType}
                timingValue={timingValue}
                setTimingValue={setTimingValue}
              />
              <Grid item xs={12}>
                <RichTextEditor
                  className={classes.textArea}
                  value={body}
                  readOnly
                  rows={textAreaSettings.rows}
                  fontSize="10px"
                  index={`${new Date().getTime()}`}
                />
              </Grid>
              <Grid item xs={12}>
                <div className={classes.buttonWrap}>
                  <Button
                    variant="text"
                    className={classes.button}
                    onClick={onSeeMoreClick}
                  >
                    {textAreaSettings.title}
                  </Button>
                </div>
              </Grid>
            </Grid>
          </div>
        </Grid>
        <Grid item xs>
          <div className={classes.line} />
        </Grid>
        {totalActions - 1 == index ? (
          <Grid item xs>
            <Fab
              color="primary"
              aria-label="add"
              onClick={() => handleDrawerToggle(true)}
            >
              <AddIcon />
            </Fab>
          </Grid>
        ) : null}
      </Grid>
    </Fragment>
  )
}

export default injectStyles(styles)(EmailTemplate)
